import React from "react";

import type { PhonemeSpan, Span } from "../../api/transcription";
import styles from "../../styles/AudioWave/AudioWave.module.scss";

import { AddTextDialog } from "./AddTextDialog";
import { AudioTimePartitionLine } from "./AudioTimePartitionLine";
import { EditTextDialog } from "./EditTextDialog";
import { PartitionLine } from "./PartitionLine";

export interface Partition {
  label: string;
  time: number;
  spanData: PhonemeSpan | Span;
}

const SVG_PADDING_X = 10;
const SVG_DEFAULT_LENGTH_SCALING = 2;
const SVG_HEIGHT = 240;

/**
 * AudioWave visualizes and allows the editing of word and phoneme partitions.
 *
 * AudioWave parameters work in the frame of 0-1. All the locations are placed within that range and then scaled by svgLength.
 *
 * @param props is described below.
 * @param props.audioRef is a reference to the audio that the AudioWave visualizes. This is used to control the audio cursor.
 * @param props.amplitudes are the amplitudes of the waveform to draw, with the first amplitude drawn at time 0 and the last amplitude drawn at time 1.
 * @param props.addPhonemePartition is called when a phoneme partition is added. The newTime parameter is in the range 0 - 1.
 * @param props.addWordPartition is called when a word partition is added. The newTime parameter is in the range 0 - 1.
 * @param props.deletePhonemePartition is called when a phoneme partition is deleted.
 * @param props.deleteWordPartition is called when a word partition is deleted.
 * @param props.editPhonemePartition is called when a phoneme partition's time is updated. The newTime parameter is in the range 0 - 1.
 * @param props.editWordPartition is called when a word partition's time is updated. The newTime parameter is in the range 0 - 1.
 * @param props.phonemePartitions are the phoneme partitions to display, with times in the range 0 - 1.
 * @param props.waveformScaleFactor is the factor by which to scale the waveform. This is used to zoom in and out.
 * @param props.wordPartitions are the word partitions to display, with times in the range 0 - 1.
 */
export const AudioWave = ({
  amplitudes,
  audioRef,
  addPhonemePartition,
  addWordPartition,
  deletePhonemePartition,
  deleteWordPartition,
  editPhonemePartition,
  editWordPartition,
  waveformScaleFactor,
  wordPartitions,
  phonemePartitions,
}: {
  amplitudes: number[][];
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  addPhonemePartition: (time: number, phoneme: string) => void;
  addWordPartition: (time: number, word: string) => void;
  deletePhonemePartition: (index: number) => void;
  deleteWordPartition: (index: number) => void;
  editPhonemePartition: (
    index: number,
    newTime: number | undefined,
    newPhoneme: string | undefined,
  ) => void;
  editWordPartition: (
    index: number,
    newTime: number | undefined,
    newWord: string | undefined,
  ) => void;
  waveformScaleFactor: number;
  wordPartitions: Partition[];
  phonemePartitions: Partition[];
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [addTime, setAddTime] = React.useState<number>();
  const [editType, setEditType] = React.useState<"phoneme" | "word">();
  const [editIndex, setEditIndex] = React.useState<number>();
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [showEditDialog, setShowEditDialog] = React.useState(false);

  // waveformScaleFactor is used by the user to zoom in and out of the waveform,
  // whereas SVG_DEFAULT_LENGTH_SCALING is a constant that makes the 1x waveform
  // look reasonable.
  const svgScalingFactor = SVG_DEFAULT_LENGTH_SCALING * waveformScaleFactor;
  const svgLength = amplitudes.length * svgScalingFactor;

  const timeToXPosition = (time: number) => time * svgLength;
  const xPositionToTime = (xPosition: number) => xPosition / svgLength;

  const wordPlacements = wordPartitions.flatMap(({ time }) =>
    timeToXPosition(time),
  );
  const phonemePlacements = phonemePartitions.flatMap(({ time }) =>
    timeToXPosition(time),
  );

  const resetAddBox = () => {
    setAddTime(undefined);
    setShowAddDialog(false);
  };
  const resetEditBox = () => {
    setEditIndex(undefined);
    setEditType(undefined);
    setShowEditDialog(false);
  };

  const renderPartitionLines = () => (
    <>
      {wordPlacements.flatMap((xPosition, index) => (
        <PartitionLine
          initialXPosition={xPosition}
          key={`word-placement-${index}`}
          label={wordPartitions[index].label}
          onLabelDoubleClick={() => {
            resetAddBox();
            setEditType("word");
            setEditIndex(index);
            setShowEditDialog(true);
          }}
          onPartitionLineMouseDown={resetEditBox}
          onPartitionLineMouseUp={mouseUpXPosition => {
            editWordPartition(
              index,
              xPositionToTime(mouseUpXPosition),
              undefined,
            );
          }}
          partitionType="word"
          svgHeight={SVG_HEIGHT}
          svgLength={svgLength}
          svgPaddingX={SVG_PADDING_X}
        />
      ))}
      {phonemePlacements.flatMap((xPosition, index) => (
        <PartitionLine
          initialXPosition={xPosition}
          key={`phoneme-placement-${index}`}
          label={phonemePartitions[index].label}
          onLabelDoubleClick={() => {
            resetAddBox();
            setEditType("phoneme");
            setEditIndex(index);
            setShowEditDialog(true);
          }}
          onPartitionLineMouseDown={resetEditBox}
          onPartitionLineMouseUp={mouseUpXPosition => {
            editPhonemePartition(
              index,
              xPositionToTime(mouseUpXPosition),
              undefined,
            );
          }}
          partitionType="phoneme"
          svgHeight={SVG_HEIGHT}
          svgLength={svgLength}
          svgPaddingX={SVG_PADDING_X}
        />
      ))}
      <AudioTimePartitionLine
        audioRef={audioRef}
        containerRef={containerRef}
        svgHeight={SVG_HEIGHT}
        svgLength={svgLength}
        svgPaddingX={SVG_PADDING_X}
      />
    </>
  );

  const renderWaveForm = () => (
    <>
      {amplitudes.flatMap((item, index) => {
        const xPosition = index * svgScalingFactor + SVG_PADDING_X;
        return (
          <line
            className={styles["highlighted-line"]}
            key={index}
            onDoubleClick={() => {
              resetEditBox();
              setAddTime(xPositionToTime(xPosition - 2 * SVG_PADDING_X));
              setShowAddDialog(true);
            }}
            strokeLinecap="round"
            strokeWidth={3 * svgScalingFactor}
            x1={xPosition}
            x2={xPosition}
            y1={SVG_HEIGHT / 2 + item[1]}
            y2={SVG_HEIGHT / 2 - item[1]}
          />
        );
      })}
    </>
  );

  const handleAddAnnotation = (
    newText: string | undefined,
    annotationType: "phoneme" | "word",
  ) => {
    if (addTime === undefined || newText === undefined) {
      alert(
        "Required field for adding annotation is undefined. Addition failed.",
      );
      throw new Error("Required field for adding annotation is undefined");
    }
    if (annotationType === "phoneme") {
      addPhonemePartition(addTime, newText);
    }
    if (annotationType === "word") {
      addWordPartition(addTime, newText);
    }
    resetAddBox();
  };

  const handleDeleteAnnotation = () => {
    if (editIndex === undefined || editType === undefined) {
      alert(
        "Required field for removing annotation is undefined. Deletion failed.",
      );
      throw new Error("Required field for removing annotation is undefined");
    }
    if (editType === "phoneme") {
      deletePhonemePartition(editIndex);
    }
    if (editType === "word") {
      deleteWordPartition(editIndex);
    }
    resetEditBox();
  };

  const handleEditAnnotation = (newText: string | undefined) => {
    if (editIndex === undefined || editType === undefined) {
      alert(
        "Required field for changing annotation is undefined. Change failed.",
      );
      throw new Error("Required field for changing annotation is undefined");
    }
    if (editType === "phoneme") {
      editPhonemePartition(editIndex, undefined, newText);
    }
    if (editType === "word") {
      editWordPartition(editIndex, undefined, newText);
    }
    resetEditBox();
  };

  const currentSelectedValue =
    editIndex === undefined
      ? ""
      : editType === "phoneme"
        ? phonemePartitions[editIndex].label
        : wordPartitions[editIndex].label;

  return (
    <>
      <AddTextDialog
        isVisible={showAddDialog}
        key={addTime}
        onAddPhoneme={text => handleAddAnnotation(text, "phoneme")}
        onAddWord={text => handleAddAnnotation(text, "word")}
        onCancel={resetAddBox}
      />
      <EditTextDialog
        editType={editType}
        isVisible={showEditDialog}
        key={`${currentSelectedValue}-${editType}-${editIndex}`}
        oldValue={currentSelectedValue}
        onCancel={resetEditBox}
        onDelete={handleDeleteAnnotation}
        onSubmit={handleEditAnnotation}
      />

      <div className={styles["wave-container"]} ref={containerRef}>
        {amplitudes ? (
          <svg
            height={SVG_HEIGHT}
            version="1.1"
            width={svgLength + SVG_PADDING_X * 2}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            {renderWaveForm()}
            {renderPartitionLines()}
          </svg>
        ) : null}
      </div>
    </>
  );
};
